import type { OuraDayDto } from '@jordan/types';

/** The metrics the dashboard can chart and compare. */
export type OuraMetricKey =
  | 'activityScore'
  | 'readinessScore'
  | 'sleepScore'
  | 'activeCalories'
  | 'restingHr'
  | 'steps'
  | 'totalCalories';

/** Numeric fields of a day that a metric can read. */
type OuraDayNumberField = {
  [K in keyof OuraDayDto]: OuraDayDto[K] extends number | null ? K : never;
}[keyof OuraDayDto];

/**
 * How a metric behaves on the intraday (time-of-day) axis:
 *  - `hr`          — real bucketed heart-rate samples
 *  - `burn-active` — the day's active calories distributed by MET weight (≈ MET − 1)
 *  - `burn-total`  — the day's total calories distributed by MET weight
 *  - `flat`        — a single daily value, held flat across the day
 */
export type OuraIntradayMode = 'hr' | 'burn-active' | 'burn-total' | 'flat';

export interface OuraMetric {
  key: OuraMetricKey;
  label: string;
  /** Field on OuraDayDto this metric reads. */
  field: OuraDayNumberField;
  /** Short unit suffix for tooltips/axis (empty for unit-less scores/steps). */
  unit: string;
  /** Series colour. */
  color: string;
  /** Format a value for display. */
  format: (v: number) => string;
  /** Optional y-axis tick interval. */
  axisInterval?: number;
  intraday: OuraIntradayMode;
}

const score = (v: number) => String(Math.round(v));
const kcal = (v: number) => `${Math.round(v).toLocaleString()} kcal`;

/** Catalog keyed by metric. Order here drives the dropdown order. */
export const OURA_METRICS: Record<OuraMetricKey, OuraMetric> = {
  activityScore: {
    key: 'activityScore',
    label: 'Activity Score',
    field: 'activityScore',
    unit: '',
    color: '#1e3a8a',
    format: score,
    axisInterval: 20,
    intraday: 'flat',
  },
  readinessScore: {
    key: 'readinessScore',
    label: 'Readiness Score',
    field: 'readinessScore',
    unit: '',
    color: '#7c3aed',
    format: score,
    axisInterval: 20,
    intraday: 'flat',
  },
  sleepScore: {
    key: 'sleepScore',
    label: 'Sleep Score',
    field: 'sleepScore',
    unit: '',
    color: '#1e293b',
    format: score,
    axisInterval: 20,
    intraday: 'flat',
  },
  activeCalories: {
    key: 'activeCalories',
    label: 'Activity Burn',
    field: 'activeCalories',
    unit: 'kcal',
    color: '#f97316',
    format: kcal,
    intraday: 'burn-active',
  },
  restingHr: {
    key: 'restingHr',
    label: 'Average RHR',
    field: 'restingHr',
    unit: 'bpm',
    color: '#38bdf8',
    format: (v) => `${Math.round(v)} bpm`,
    intraday: 'hr',
  },
  steps: {
    key: 'steps',
    label: 'Steps',
    field: 'steps',
    unit: '',
    color: '#0d9488',
    format: (v) => Math.round(v).toLocaleString(),
    intraday: 'flat',
  },
  totalCalories: {
    key: 'totalCalories',
    label: 'Total Burn',
    field: 'totalCalories',
    unit: 'kcal',
    color: '#dc2626',
    format: kcal,
    intraday: 'burn-total',
  },
};

/** Ordered list for dropdowns. */
export const OURA_METRIC_OPTIONS = Object.values(OURA_METRICS).map((m) => ({
  value: m.key,
  label: m.label,
}));

export const isOuraMetricKey = (v: string): v is OuraMetricKey => v in OURA_METRICS;
